import React, { useState, useEffect, useRef, useCallback } from 'react';
import { TelnyxRTC } from '@telnyx/webrtc';
import { Phone, X, Minimize2, Maximize2, AlertCircle } from 'lucide-react';
import { dialerAPI, endUserAPI, reportsAPI } from '../../services/api';
import { useNotificationSound } from '../../hooks/useNotificationSound';
import { ConnectionStatus, CONNECTION_STATES } from './ConnectionStatus';
import { IncomingCallModal } from './IncomingCallModal';
import { MinimizedDialerView } from './MinimizedDialerView';
import { DialPad } from './DialPad';
import { CallControls, CALL_STATES } from './CallControls';
import { CallHistoryPopover } from './CallHistoryPopover';

const WebRTCDialer = ({
  userId,
  userType = 'client',
  onCallStart,
  onCallEnd,
  minimized: initialMinimized = false,
  showAIAssist = true,
  initialNumber = '',
}) => {
  const { playSound, startRingtone, stopRingtone } = useNotificationSound();

  // State
  const [minimized, setMinimized] = useState(initialMinimized);
  const [callState, setCallState] = useState(CALL_STATES.IDLE);
  const [connectionState, setConnectionState] = useState(CONNECTION_STATES.DISCONNECTED);
  const [phoneNumber, setPhoneNumber] = useState(initialNumber);
  const [callerInfo, setCallerInfo] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isSpeakerOn, setIsSpeakerOn] = useState(true);
  const [callDuration, setCallDuration] = useState(0);
  const [showDialpad, setShowDialpad] = useState(false);
  const [showTransfer, setShowTransfer] = useState(false);
  const [transferNumber, setTransferNumber] = useState('');
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [selectedFromNumber, setSelectedFromNumber] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [incomingCall, setIncomingCall] = useState(null);

  // AI Assist state
  const [aiSuggestions] = useState([]);
  const [sentiment] = useState('neutral');

  // Call history popover state
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');

  // Refs
  const timerRef = useRef(null);
  const telnyxClientRef = useRef(null);
  const currentCallRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const initInFlightRef = useRef(false);
  const initAttemptedRef = useRef(false);

  // Fetch available phone numbers
  useEffect(() => {
    const fetchPhoneNumbers = async () => {
      try {
        const response = userType === 'client'
          ? await dialerAPI.getPhoneNumbers(userId)
          : await endUserAPI.getWebRTCToken(userId);

        if (response.data.success) {
          setPhoneNumbers(response.data.phone_numbers || []);
          setSelectedFromNumber(response.data.default || '');
        }
      } catch (err) {
        console.error('Error fetching phone numbers:', err);
      }
    };

    if (userId) {
      fetchPhoneNumbers();
    }
  }, [userId, userType]);

  // Fetch call history (lazy — only when popover opens)
  const fetchHistory = useCallback(async () => {
    if (!userId) return;
    setHistoryLoading(true);
    setHistoryError('');
    try {
      const response = userType === 'client'
        ? await reportsAPI.getCalls(userId, { limit: 10 })
        : await endUserAPI.getCallHistory(userId);
      const calls = response?.data?.calls || [];
      setHistory(calls.slice(0, 10));
    } catch (err) {
      console.error('Error fetching call history:', err);
      setHistoryError(err?.response?.data?.detail || 'Failed to load call history');
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [userId, userType]);

  const handleToggleHistory = useCallback(() => {
    setShowHistory((prev) => {
      const next = !prev;
      if (next) fetchHistory();
      return next;
    });
  }, [fetchHistory]);

  const handleRedial = useCallback((number) => {
    if (!number) return;
    setPhoneNumber(number);
    setShowHistory(false);
  }, []);

  // Handle call end - defined first to avoid reference error
  const handleCallEnd = useCallback(() => {
    setCallState(CALL_STATES.ENDED);

    if (onCallEnd) {
      onCallEnd({ duration: callDuration });
    }

    setTimeout(() => {
      setCallState(CALL_STATES.IDLE);
      setCallerInfo(null);
      setIncomingCall(null);
      setPhoneNumber('');
      currentCallRef.current = null;
    }, 1500);
  }, [callDuration, onCallEnd]);

  // Handle call state updates
  const handleCallUpdate = useCallback((call) => {
    if (!call) return;

    try {
      console.log('Call update:', call.state, call);
      currentCallRef.current = call;

      if (call.remoteStream && remoteAudioRef.current) {
        remoteAudioRef.current.srcObject = call.remoteStream;
        remoteAudioRef.current.play().catch((e) => console.log('Audio play error:', e));
      }

      switch (call.state) {
        case 'new':
          if (call.direction === 'inbound') {
            const incomingCallerInfo = {
              number: call.options?.remoteCallerNumber || 'Unknown',
              name: call.options?.remoteCallerName || 'Incoming Call',
              direction: 'inbound',
            };
            setIncomingCall(call);
            setCallerInfo(incomingCallerInfo);
            startRingtone(incomingCallerInfo);
          }
          break;
        case 'trying':
          setCallState(CALL_STATES.CONNECTING);
          break;
        case 'ringing':
        case 'early':
          setCallState(CALL_STATES.RINGING);
          break;
        case 'active':
          stopRingtone();
          setCallState(CALL_STATES.CONNECTED);
          setIncomingCall(null);
          playSound('callConnected');
          if (onCallStart) {
            onCallStart({
              id: call.id || 'unknown',
              direction: call.direction || 'outbound',
              number: callerInfo?.number || phoneNumber,
            });
          }
          break;
        case 'held':
          setCallState(CALL_STATES.ON_HOLD);
          playSound('toggle');
          break;
        case 'hangup':
        case 'destroy':
        case 'purge':
          stopRingtone();
          playSound('callEnded');
          handleCallEnd();
          break;
        default:
          console.log('Unknown call state:', call.state);
      }
    } catch (err) {
      console.error('Error in handleCallUpdate:', err);
    }
  }, [onCallStart, phoneNumber, callerInfo, handleCallEnd, playSound, startRingtone, stopRingtone]);

  // Initialize Telnyx WebRTC client
  const initializeTelnyxClient = useCallback(async () => {
    if (telnyxClientRef.current) {
      console.log('Telnyx client already initialized');
      return true;
    }
    if (initInFlightRef.current) {
      console.log('Telnyx client init already in flight');
      return false;
    }
    initInFlightRef.current = true;

    setConnectionState(CONNECTION_STATES.CONNECTING);

    try {
      const response = userType === 'client'
        ? await dialerAPI.getToken(userId, userId)
        : await endUserAPI.getWebRTCToken(userId);

      if (!response.data.success || !response.data.token) {
        throw new Error('Failed to get WebRTC token');
      }

      const { token } = response.data;

      console.log('Initializing Telnyx WebRTC client...');
      const client = new TelnyxRTC({ login_token: token });

      // Track last SDK event so we can surface useful diagnostics when the
      // socket closes before reaching the "ready" state.
      const sdkState = { lastError: null, gotReady: false };

      client.on('telnyx.ready', () => {
        console.log('[TelnyxWebRTC] telnyx.ready fired');
        sdkState.gotReady = true;
        setConnectionState(CONNECTION_STATES.CONNECTED);
        setError('');
      });

      client.on('telnyx.error', (err) => {
        const code = err?.errorCode || err?.code || err?.error?.code;
        const msg = err?.message || err?.error?.message || JSON.stringify(err);
        console.error(`[TelnyxWebRTC] telnyx.error code=${code} msg=${msg}`, err);
        sdkState.lastError = { code, msg };
        if (!sdkState.gotReady) {
          setError(`Telnyx error${code ? ` ${code}` : ''}: ${msg}`);
          setConnectionState(CONNECTION_STATES.ERROR);
        }
      });

      client.on('telnyx.socket.error', (err) => {
        // Browser Event objects don't stringify usefully — pull useful fields
        const wsUrl = err?.target?.url || err?.url;
        const readyState = err?.target?.readyState;
        const msg = err?.message || `WebSocket error (readyState=${readyState}, url=${wsUrl || 'rtc.telnyx.com'})`;
        console.error('[TelnyxWebRTC] telnyx.socket.error', { wsUrl, readyState, raw: err });
        sdkState.lastError = { code: 'SOCKET_ERROR', msg };
        if (!sdkState.gotReady) {
          setError(`WebSocket error: ${msg}. The browser could not establish the Telnyx WSS handshake — usually a network/proxy block or an invalid login token.`);
          setConnectionState(CONNECTION_STATES.ERROR);
        }
      });

      client.on('telnyx.socket.close', (ev) => {
        const closeCode = ev?.code;
        const closeReason = ev?.reason || '';
        const wsUrl = ev?.target?.url;
        console.log(`[TelnyxWebRTC] telnyx.socket.close code=${closeCode} reason="${closeReason}" url=${wsUrl} gotReady=${sdkState.gotReady}`);
        if (!sdkState.gotReady) {
          const lastErr = sdkState.lastError;
          let detail;
          if (lastErr && lastErr.code !== 'SOCKET_ERROR') {
            detail = `Telnyx ${lastErr.code || 'error'}: ${lastErr.msg}`;
          } else if (closeCode === 1006) {
            detail = `WebSocket closed abnormally (code 1006) before SIP register — Telnyx server closed the connection. Most common cause: login_token rejected by Telnyx (try Retry to refresh token), or the SIP connection on Telnyx is misconfigured.`;
          } else if (closeCode && closeCode !== 1000) {
            detail = `WebSocket closed before registration (code ${closeCode}${closeReason ? ', ' + closeReason : ''}).`;
          } else {
            detail = `WebSocket closed before SIP register completed. Token may be expired or the Telnyx credential connection is misconfigured.`;
          }
          setError(detail);
          setConnectionState(CONNECTION_STATES.ERROR);
        } else {
          setConnectionState(CONNECTION_STATES.DISCONNECTED);
        }
      });

      client.on('telnyx.notification', (notification) => {
        console.log('[TelnyxWebRTC] telnyx.notification:', notification?.type, notification);

        if (notification.type === 'callUpdate') {
          handleCallUpdate(notification.call);
        } else if (notification.type === 'userMediaError') {
          const errorMsg = notification.errorMessage || 'Microphone access denied';
          console.error('[TelnyxWebRTC] Media error:', errorMsg);
          setError(`Media error: ${errorMsg}. Please ensure microphone access is allowed.`);
          setCallState(CALL_STATES.IDLE);
        }
      });

      await client.connect();
      telnyxClientRef.current = client;
      console.log('[TelnyxWebRTC] client.connect() returned — awaiting telnyx.ready event');
      initInFlightRef.current = false;
      return true;
    } catch (err) {
      console.error('Failed to initialize Telnyx client:', err);
      const backendDetail = err?.response?.data?.detail;
      setError(backendDetail || err.message || 'Failed to connect to calling service');
      setConnectionState(CONNECTION_STATES.ERROR);
      initInFlightRef.current = false;
      return false;
    }
  }, [userId, userType, handleCallUpdate]);

  // Stable ref to the latest initializeTelnyxClient so the mount effect below
  // never re-runs its cleanup just because `handleCallUpdate` (which depends
  // on `phoneNumber`, `callerInfo`, etc.) was recreated.
  const initializeTelnyxClientRef = useRef(initializeTelnyxClient);
  useEffect(() => {
    initializeTelnyxClientRef.current = initializeTelnyxClient;
  }, [initializeTelnyxClient]);

  // Auto-connect on mount (one-shot — manual reconnect via button if it fails).
  // Depends ONLY on `userId` so a re-render that recreates `handleCallUpdate`
  // (e.g. user types into the dial pad) does not unmount-then-remount the
  // Telnyx client and silently kill the live connection.
  useEffect(() => {
    if (userId && !initAttemptedRef.current) {
      initAttemptedRef.current = true;
      initializeTelnyxClientRef.current?.();
    }

    return () => {
      if (telnyxClientRef.current) {
        telnyxClientRef.current.disconnect();
        telnyxClientRef.current = null;
      }
    };
  }, [userId]);

  // Manual reconnect handler (clears guard so user can retry on demand)
  const handleReconnect = useCallback(() => {
    if (initInFlightRef.current) return;
    setError('');
    setConnectionState(CONNECTION_STATES.DISCONNECTED);
    if (telnyxClientRef.current) {
      try { telnyxClientRef.current.disconnect(); } catch (e) { /* noop */ }
      telnyxClientRef.current = null;
    }
    initAttemptedRef.current = false;
    initializeTelnyxClientRef.current?.();
  }, []);

  // Update phone number when initialNumber prop changes
  useEffect(() => {
    if (initialNumber && callState === CALL_STATES.IDLE) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- sync prop to local input
      setPhoneNumber(initialNumber);
    }
  }, [initialNumber, callState]);

  // Call duration timer
  useEffect(() => {
    if (callState === CALL_STATES.CONNECTED) {
      timerRef.current = setInterval(() => {
        setCallDuration((prev) => prev + 1);
      }, 1000);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
      if (callState === CALL_STATES.IDLE) {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- reset timer when call ends
        setCallDuration(0);
      }
    }

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [callState]);

  // Make outbound call
  const makeCall = async () => {
    if (!phoneNumber.trim()) {
      setError('Please enter a phone number');
      return;
    }

    setLoading(true);
    setError('');

    try {
      if (!telnyxClientRef.current || connectionState !== CONNECTION_STATES.CONNECTED) {
        const initialized = await initializeTelnyxClient();
        if (!initialized) {
          setLoading(false);
          return;
        }
      }

      let formattedNumber = phoneNumber.trim();
      if (!formattedNumber.startsWith('+')) {
        formattedNumber = '+1' + formattedNumber.replace(/\D/g, '');
      }

      setCallerInfo({
        number: formattedNumber,
        name: 'Outgoing Call',
        direction: 'outbound',
      });

      setCallState(CALL_STATES.CONNECTING);

      const callOptions = {
        destinationNumber: formattedNumber,
        callerNumber: selectedFromNumber || undefined,
      };

      console.log('Making call with options:', callOptions);
      const call = telnyxClientRef.current.newCall(callOptions);
      currentCallRef.current = call;
      console.log('Call initiated:', call);
    } catch (err) {
      console.error('Call error:', err);
      setError(err.message || 'Failed to make call');
      setCallState(CALL_STATES.IDLE);
    } finally {
      setLoading(false);
    }
  };

  const answerCall = () => {
    if (incomingCall) {
      stopRingtone();
      incomingCall.answer();
      setIncomingCall(null);
    }
  };

  const rejectCall = () => {
    if (incomingCall) {
      stopRingtone();
      playSound('callEnded');
      incomingCall.hangup();
      setIncomingCall(null);
      setCallerInfo(null);
    }
  };

  const endCall = useCallback(() => {
    if (currentCallRef.current) {
      currentCallRef.current.hangup();
    } else {
      handleCallEnd();
    }
  }, [handleCallEnd]);

  const toggleMute = () => {
    if (currentCallRef.current) {
      if (isMuted) currentCallRef.current.unmuteAudio();
      else currentCallRef.current.muteAudio();
    }
    setIsMuted((prev) => !prev);
  };

  const toggleSpeaker = () => {
    if (remoteAudioRef.current) {
      remoteAudioRef.current.muted = isSpeakerOn;
    }
    setIsSpeakerOn((prev) => !prev);
  };

  const toggleHold = () => {
    if (currentCallRef.current) {
      if (callState === CALL_STATES.ON_HOLD) currentCallRef.current.unhold();
      else currentCallRef.current.hold();
    }
  };

  const sendDTMF = (digit) => {
    if (currentCallRef.current && callState === CALL_STATES.CONNECTED) {
      currentCallRef.current.dtmf(digit);
    }
    setPhoneNumber((prev) => prev + digit);
  };

  const transferCall = async () => {
    if (!transferNumber.trim() || !currentCallRef.current) return;

    let formattedNumber = transferNumber.trim();
    if (!formattedNumber.startsWith('+')) {
      formattedNumber = '+1' + formattedNumber.replace(/\D/g, '');
    }

    try {
      await currentCallRef.current.transfer(formattedNumber);
      setShowTransfer(false);
      setTransferNumber('');
    } catch (err) {
      console.error('Transfer error:', err);
      setError('Failed to transfer call');
    }
  };

  const reconnect = handleReconnect;

  // Render incoming call modal
  if (incomingCall && callState === CALL_STATES.IDLE) {
    return (
      <IncomingCallModal
        callerNumber={callerInfo?.number}
        onAnswer={answerCall}
        onReject={rejectCall}
      />
    );
  }

  // Render minimized view
  if (minimized && callState !== CALL_STATES.IDLE) {
    return (
      <MinimizedDialerView
        callState={callState}
        callDuration={callDuration}
        callerNumber={callerInfo?.number}
        audioRef={remoteAudioRef}
        onExpand={() => setMinimized(false)}
        onHangup={endCall}
      />
    );
  }

  return (
    <div
      className={`bg-[#0d1117] border border-gray-800 rounded-xl shadow-2xl overflow-hidden ${minimized ? 'w-80' : 'w-96'}`}
      data-testid="webrtc-dialer"
    >
      <audio ref={remoteAudioRef} autoPlay />

      <div className="flex items-center justify-between p-4 border-b border-gray-800 bg-gradient-to-r from-teal-500/10 to-blue-500/10">
        <div className="flex items-center gap-2">
          <Phone className="w-5 h-5 text-teal-400" />
          <span className="text-white font-semibold">Dialer</span>
        </div>
        <div className="flex items-center gap-2">
          <ConnectionStatus connectionState={connectionState} onReconnect={reconnect} />
          {callState !== CALL_STATES.IDLE && (
            <button
              onClick={() => setMinimized(true)}
              className="p-1.5 hover:bg-gray-800 rounded-lg transition-colors"
              data-testid="dialer-minimize-btn"
            >
              <Minimize2 className="w-4 h-4 text-gray-400" />
            </button>
          )}
          {minimized && (
            <button
              onClick={() => setMinimized(false)}
              className="p-1.5 hover:bg-gray-800 rounded-lg transition-colors"
              data-testid="dialer-maximize-btn"
            >
              <Maximize2 className="w-4 h-4 text-gray-400" />
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="mx-4 mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-2" data-testid="dialer-error">
          <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
          <span className="text-red-400 text-sm flex-1">{error}</span>
          <button onClick={() => setError('')} className="text-red-400 hover:text-red-300" data-testid="dialer-error-dismiss">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="p-4 relative">
        {callState === CALL_STATES.IDLE ? (
          <DialPad
            phoneNumber={phoneNumber}
            onPhoneNumberChange={setPhoneNumber}
            phoneNumbers={phoneNumbers}
            selectedFromNumber={selectedFromNumber}
            onSelectedFromNumberChange={setSelectedFromNumber}
            connectionState={connectionState}
            loading={loading}
            onCall={makeCall}
            onToggleHistory={handleToggleHistory}
          />
        ) : (
          <CallControls
            callState={callState}
            callDuration={callDuration}
            callerNumber={callerInfo?.number}
            isMuted={isMuted}
            isSpeakerOn={isSpeakerOn}
            showDialpad={showDialpad}
            showTransfer={showTransfer}
            transferNumber={transferNumber}
            aiSuggestions={aiSuggestions}
            sentiment={sentiment}
            showAIAssist={showAIAssist}
            onToggleMute={toggleMute}
            onToggleSpeaker={toggleSpeaker}
            onToggleHold={toggleHold}
            onToggleDialpad={() => setShowDialpad((prev) => !prev)}
            onSendDTMF={sendDTMF}
            onToggleTransfer={() => setShowTransfer((prev) => !prev)}
            onTransferNumberChange={setTransferNumber}
            onTransfer={transferCall}
            onEndCall={endCall}
          />
        )}

        {showHistory && callState === CALL_STATES.IDLE && (
          <CallHistoryPopover
            calls={history}
            loading={historyLoading}
            error={historyError}
            onRedial={handleRedial}
            onClose={() => setShowHistory(false)}
          />
        )}
      </div>
    </div>
  );
};

export default WebRTCDialer;
